import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Route, Routes, Navigate, useLocation } from "react-router-dom";
import {
  BackArrow,
  TopProgress,
  Tabs,
  Select,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import ComputeQuotasTab from "./compute-quotas";
import VolumeQuotasTab from "./volume-quotas";
import NetworkQuotasTab from "./network-quotas";
import DatabaseQuotasTab from "./database-quotas";
import {
  Container,
  HeadWrapper,
  PageTitle,
  NavWrapper,
} from "./styles";

const ProjectQuotas: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { viewAlert } = useUi();
  const { id } = useParams<{ id: string }>();

  const [project, setProject] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [regions, setRegions] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  usePageTitle(project?.name ? `${t("EDIT_PROJECT_QUOTAS")}: ${project.name}` : t("EDIT_PROJECT_QUOTAS"));

  const getActiveTabFromPath = () => {
    const path = location.pathname.split('/').pop();
    return path || 'compute';
  };

  const [activeTab, setActiveTab] = useState<string>(getActiveTabFromPath());

  useEffect(() => {
    const currentTab = getActiveTabFromPath();
    setActiveTab(currentTab);
  }, [location.pathname]);

  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    navigate(`/openstack/projects/${id}/quotas/${tabSlug}`);
  };

  useEffect(() => {
    if (!id) {
      navigate("/openstack/projects");
      return;
    }

    Promise.all([
      projectService.getProjectDetail(id),
      projectService.getRegions(),
    ])
      .then(([projectRes, regionsRes]) => {
        setProject(projectRes.data);
        const regionsList = regionsRes.data?.objects || regionsRes.data || [];
        setRegions(regionsList);
        if (regionsList.length > 0) {
          setSelectedRegion(regionsList[0].id);
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_PROJECT");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/projects");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [id]);

  const tabs = [
    {
      title: t("COMPUTE_QUOTAS"),
      slug: "compute",
      id: "1",
    },
    {
      title: t("VOLUME_QUOTAS"),
      slug: "volume",
      id: "2",
    },
    {
      title: t("NETWORK_QUOTAS"),
      slug: "network",
      id: "3",
    },
    {
      title: t("DATABASE_QUOTAS"),
      slug: "database",
      id: "4",
    },
  ];

  const regionOptions = regions.map((r: any) => ({
    value: r.id,
    label: r.id,
  }));

  if (pageLoading || !project || !selectedRegion) {
    return <TopProgress loading={pageLoading} />;
  }

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <BackArrow fallbackPath="/openstack/projects" />
      <HeadWrapper>
        <PageTitle>
          {t("EDIT_PROJECT_QUOTAS")}: {project.name}
        </PageTitle>
        {regions.length > 1 && (
          <div style={{ minWidth: '200px' }}>
            <Select
              title={t("REGION")}
              data={regionOptions}
              selectedValue={selectedRegion}
              onChange={(val: any) => setSelectedRegion(val)}
              toolTip={false}
            />
          </div>
        )}
      </HeadWrapper>
      <NavWrapper>
        <div>
          <Tabs
            tabs={tabs}
            size="display"
            location={activeTab}
            onChange={handleTabChange}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to="compute" />} />
        <Route path="*" element={<Navigate replace to="compute" />} />
        <Route path="/compute" element={<ComputeQuotasTab projectId={id!} regionId={selectedRegion} />} />
        <Route path="/volume" element={<VolumeQuotasTab projectId={id!} regionId={selectedRegion} />} />
        <Route path="/network" element={<NetworkQuotasTab projectId={id!} regionId={selectedRegion} />} />
        <Route path="/database" element={<DatabaseQuotasTab projectId={id!} regionId={selectedRegion} />} />
      </Routes>
    </Container>
  );
};

export default ProjectQuotas;
